// fabricShapes.js
import { fabric } from 'fabric';

// Helper to calculate polygon points based on sides and radius
export function calculatePolygonPoints(sides, radius, angleOffset = -Math.PI / 2) {
  return Array.from({ length: sides }).map((_, i) => {
    const angle = (2 * Math.PI / sides) * i + angleOffset;
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
    };
  });
}

// Helper to register polygon-based shapes
function registerPolygonShape(name, sides, defaultRadius = 60) {
  fabric[name] = fabric.util.createClass(fabric.Polygon, {
    type: name.toLowerCase(),
    initialize(options = {}) {
      const radius = options.radius || defaultRadius;
      const points = options.points || calculatePolygonPoints(sides, radius);
      this.callSuper('initialize', points, {
        ...options,
        type: name.toLowerCase(),
        radius,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric[name].fromObject = (object, callback) => {
    if (!object.points) {
      console.warn(`${name} object missing points, calculating based on radius:`, object);
      object.points = calculatePolygonPoints(sides, object.radius || defaultRadius);
    }
    const instance = new fabric[name]({
      ...object,
      points: object.points,
      radius: object.radius || defaultRadius,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject[name.toLowerCase()] = fabric[name];
}

// Register polygon-based shapes
if (!fabric.Pentagon) registerPolygonShape('Pentagon', 5, 60);
if (!fabric.Hexagon) registerPolygonShape('Hexagon', 6, 60);
if (!fabric.Heptagon) registerPolygonShape('Heptagon', 7, 60);
if (!fabric.Octagon) registerPolygonShape('Octagon', 8, 60);

// Register Star (10-point star with inner and outer radius)
if (!fabric.Star) {
  fabric.Star = fabric.util.createClass(fabric.Polygon, {
    type: 'star',
    initialize(options = {}) {
      const radius = options.radius || 60;
      const points = options.points || (() => {
        const outerRadius = radius;
        const innerRadius = radius * 0.5;
        const points = [];
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? outerRadius : innerRadius;
          const angle = (Math.PI / 5) * i - Math.PI / 2;
          points.push({
            x: r * Math.cos(angle),
            y: r * Math.sin(angle),
          });
        }
        return points;
      })();
      this.callSuper('initialize', points, {
        ...options,
        type: 'star',
        radius,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric.Star.fromObject = (object, callback) => {
    if (!object.points) {
      console.warn('Star object missing points, calculating based on radius:', object);
      const radius = object.radius || 60;
      const outerRadius = radius;
      const innerRadius = radius * 0.5;
      const points = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (Math.PI / 5) * i - Math.PI / 2;
        points.push({
          x: r * Math.cos(angle),
          y: r * Math.sin(angle),
        });
      }
      object.points = points;
    }
    const instance = new fabric.Star({
      ...object,
      points: object.points,
      radius: object.radius || 60,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['star'] = fabric.Star;
}

// Register Diamond
if (!fabric.Diamond) {
  fabric.Diamond = fabric.util.createClass(fabric.Polygon, {
    type: 'diamond',
    initialize(options = {}) {
      const size = options.radius || 50;
      const points = options.points || [
        { x: 0, y: -size },
        { x: size, y: 0 },
        { x: 0, y: size },
        { x: -size, y: 0 },
      ];
      this.callSuper('initialize', points, {
        ...options,
        type: 'diamond',
        radius: size,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric.Diamond.fromObject = (object, callback) => {
    if (!object.points) {
      console.warn('Diamond object missing points, calculating based on radius:', object);
      const size = object.radius || 50;
      object.points = [
        { x: 0, y: -size },
        { x: size, y: 0 },
        { x: 0, y: size },
        { x: -size, y: 0 },
      ];
    }
    const instance = new fabric.Diamond({
      ...object,
      points: object.points,
      radius: object.radius || 50,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['diamond'] = fabric.Diamond;
}

// Register Chevron
if (!fabric.Chevron) {
  fabric.Chevron = fabric.util.createClass(fabric.Polygon, {
    type: 'chevron',
    initialize(options = {}) {
      const size = options.radius || 60;
      const points = options.points || [
        { x: -size, y: -size * 0.5 },
        { x: 0, y: -size * 0.5 },
        { x: size, y: 0 },
        { x: 0, y: size * 0.5 },
        { x: -size * 0.5, y: size * 0.5 },
        { x: -size, y: size * 0.25 },
        { x: -size * 0.5, y: 0 },
      ];
      this.callSuper('initialize', points, {
        ...options,
        type: 'chevron',
        radius: size,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric.Chevron.fromObject = (object, callback) => {
    if (!object.points) {
      console.warn('Chevron object missing points, calculating based on radius:', object);
      const size = object.radius || 60;
      object.points = [
        { x: -size, y: -size * 0.5 },
        { x: 0, y: -size * 0.5 },
        { x: size, y: 0 },
        { x: 0, y: size * 0.5 },
        { x: -size * 0.5, y: size * 0.5 },
        { x: -size, y: size * 0.25 },
        { x: -size * 0.5, y: 0 },
      ];
    }
    const instance = new fabric.Chevron({
      ...object,
      points: object.points,
      radius: object.radius || 60,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['chevron'] = fabric.Chevron;
}

// Register Trapezoid
if (!fabric.Trapezoid) {
  fabric.Trapezoid = fabric.util.createClass(fabric.Polygon, {
    type: 'trapezoid',
    initialize(options = {}) {
      const size = options.radius || 60;
      const points = options.points || [
        { x: -size, y: -size / 2 },
        { x: size, y: -size / 2 },
        { x: size * 0.6, y: size / 2 },
        { x: -size * 0.6, y: size / 2 },
      ];
      this.callSuper('initialize', points, {
        ...options,
        type: 'trapezoid',
        radius: size,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric.Trapezoid.fromObject = (object, callback) => {
    if (!object.points) {
      console.warn('Trapezoid object missing points, calculating based on radius:', object);
      const size = object.radius || 60;
      object.points = [
        { x: -size, y: -size / 2 },
        { x: size, y: -size / 2 },
        { x: size * 0.6, y: size / 2 },
        { x: -size * 0.6, y: size / 2 },
      ];
    }
    const instance = new fabric.Trapezoid({
      ...object,
      points: object.points,
      radius: object.radius || 60,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['trapezoid'] = fabric.Trapezoid;
}

// Register Parallelogram
if (!fabric.Parallelogram) {
  fabric.Parallelogram = fabric.util.createClass(fabric.Polygon, {
    type: 'parallelogram',
    initialize(options = {}) {
      const size = options.radius || 60;
      const points = options.points || [
        { x: -size, y: -size / 2 },
        { x: size * 0.5, y: -size / 2 },
        { x: size, y: size / 2 },
        { x: -size * 0.5, y: size / 2 },
      ];
      this.callSuper('initialize', points, {
        ...options,
        type: 'parallelogram',
        radius: size,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric.Parallelogram.fromObject = (object, callback) => {
    if (!object.points) {
      console.warn('Parallelogram object missing points, calculating based on radius:', object);
      const size = object.radius || 60;
      object.points = [
        { x: -size, y: -size / 2 },
        { x: size * 0.5, y: -size / 2 },
        { x: size, y: size / 2 },
        { x: -size * 0.5, y: size / 2 },
      ];
    }
    const instance = new fabric.Parallelogram({
      ...object,
      points: object.points,
      radius: object.radius || 60,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['parallelogram'] = fabric.Parallelogram;
}

// Register Heart
if (!fabric.Heart) {
  fabric.Heart = fabric.util.createClass(fabric.Path, {
    type: 'heart',
    initialize(options = {}) {
      const size = options.radius || 16; // Base size from viewBox (32x32), scaled to radius
      const path = 'M23.6,0C20.4,0,17.9,2.1,16,5.1C14.1,2.1,11.6,0,8.4,0C3.8,0,0,3.8,0,8.4c0,4.5,3.4,8.2,8.5,13.1c2.4,2.2,5.1,4.4,7.5,6.5c2.4-2.1,5.1-4.3,7.5-6.5c5.1-4.9,8.5-8.6,8.5-13.1C32,3.8,28.2,0,23.6,0z';
      const scale = (options.radius || 16) / 16;
      this.callSuper('initialize', path, {
        ...options,
        type: 'heart',
        scaleX: options.scaleX || scale,
        scaleY: options.scaleY || scale,
        originX: 'center',
        originY: 'center',
      });
      this.set({ left: options.left || 0, top: options.top || 0 });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius || 16,
        scaleX: this.scaleX,
        scaleY: this.scaleY,
      });
    },
  });

  fabric.Heart.fromObject = (object, callback) => {
    const path = 'M23.6,0C20.4,0,17.9,2.1,16,5.1C14.1,2.1,11.6,0,8.4,0C3.8,0,0,3.8,0,8.4c0,4.5,3.4,8.2,8.5,13.1c2.4,2.2,5.1,4.4,7.5,6.5c2.4-2.1,5.1-4.3,7.5-6.5c5.1-4.9,8.5-8.6,8.5-13.1C32,3.8,28.2,0,23.6,0z';
    const instance = new fabric.Heart({
      ...object,
      path: path,
      scaleX: object.scaleX, // Use stored scaleX, no fallback to radius-based scale
      scaleY: object.scaleY, // Use stored scaleY, no fallback to radius-based scale
      originX: 'center',
      originY: 'center',
      radius: object.radius || 16,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['heart'] = fabric.Heart;
}

// Register Cross
if (!fabric.Cross) {
  fabric.Cross = fabric.util.createClass(fabric.Polygon, {
    type: 'cross',
    initialize(options = {}) {
      const size = options.radius || 60;
      const arm = size * 0.3;
      const points = options.points || [
        { x: -arm, y: -size },
        { x: arm, y: -size },
        { x: arm, y: -arm },
        { x: size, y: -arm },
        { x: size, y: arm },
        { x: arm, y: arm },
        { x: arm, y: size },
        { x: -arm, y: size },
        { x: -arm, y: arm },
        { x: -size, y: arm },
        { x: -size, y: -arm },
        { x: -arm, y: -arm },
      ];
      this.callSuper('initialize', points, {
        ...options,
        type: 'cross',
        radius: size,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric.Cross.fromObject = (object, callback) => {
    if (!object.points) {
      console.warn('Cross object missing points, calculating based on radius:', object);
      const size = object.radius || 60;
      const arm = size * 0.3;
      object.points = [
        { x: -arm, y: -size },
        { x: arm, y: -size },
        { x: arm, y: -arm },
        { x: size, y: -arm },
        { x: size, y: arm },
        { x: arm, y: arm },
        { x: arm, y: size },
        { x: -arm, y: size },
        { x: -arm, y: arm },
        { x: -size, y: arm },
        { x: -size, y: -arm },
        { x: -arm, y: -arm },
      ];
    }
    const instance = new fabric.Cross({
      ...object,
      points: object.points,
      radius: object.radius || 60,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['cross'] = fabric.Cross;
}

// Register Semicircle
if (!fabric.Semicircle) {
  fabric.Semicircle = fabric.util.createClass(fabric.Path, {
    type: 'semicircle',
    initialize(options = {}) {
      const radius = options.radius || 60;
      const path = `
        M -${radius} 0
        A ${radius} ${radius} 0 0 1 ${radius} 0
        L 0 0
        Z
      `;
      this.callSuper('initialize', path, {
        ...options,
        type: 'semicircle',
        fill: '',
        stroke: '#000',
        strokeWidth: 1,
        originX: 'center',
        originY: 'center',
        radius,
      });
      this.set({ left: options.left || 0, top: options.top || 0 });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
      });
    },
  });

  fabric.Semicircle.fromObject = (object, callback) => {
    const radius = object.radius || 60;
    const path = `
      M -${radius} 0
      A ${radius} ${radius} 0 0 1 ${radius} 0
      L 0 0
      Z
    `;
    const instance = new fabric.Semicircle({
      ...object,
      path: path,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      originX: 'center',
      originY: 'center',
      radius: radius,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['semicircle'] = fabric.Semicircle;
}

// Register Pentagram
if (!fabric.Pentagram) {
  fabric.Pentagram = fabric.util.createClass(fabric.Polygon, {
    type: 'pentagram',
    initialize(options = {}) {
      const radius = options.radius || 60;
      const points = options.points || (() => {
        const basePoints = calculatePolygonPoints(5, radius);
        const order = [0, 2, 4, 1, 3];
        return order.map(i => basePoints[i]);
      })();
      this.callSuper('initialize', points, {
        ...options,
        type: 'pentagram',
        fill: '#fff',
        stroke: '#333',
        strokeWidth: 2,
        radius,
      });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
        points: this.points,
      });
    },
  });

  fabric.Pentagram.fromObject = (object, callback) => {
    if (!object.points) {
      console.warn('Pentagram object missing points, calculating based on radius:', object);
      const radius = object.radius || 60;
      const basePoints = calculatePolygonPoints(5, radius);
      const order = [0, 2, 4, 1, 3];
      object.points = order.map(i => basePoints[i]);
    }
    const instance = new fabric.Pentagram({
      ...object,
      points: object.points,
      fill: '#fff',
      stroke: '#333',
      strokeWidth: 2,
      radius: object.radius || 60,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['pentagram'] = fabric.Pentagram;
}

// Register Crescent
if (!fabric.Crescent) {
  fabric.Crescent = fabric.util.createClass(fabric.Path, {
    type: 'crescent',
    initialize(options = {}) {
      const radius = options.radius || 60;
      const innerRadius = radius * 0.6;
      const path = `
        M ${radius} 0
        A ${radius} ${radius} 0 0 1 ${-radius} 0
        A ${innerRadius} ${innerRadius} 0 0 0 ${radius} 0
        Z
      `;
      this.callSuper('initialize', path, {
        ...options,
        type: 'crescent',
        fill: '',
        stroke: '#000',
        strokeWidth: 1,
        originX: 'center',
        originY: 'center',
        radius,
      });
      this.set({ left: options.left || 0, top: options.top || 0 });
    },
    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        id: this.id,
        type: this.type,
        radius: this.radius,
      });
    },
  });

  fabric.Crescent.fromObject = (object, callback) => {
    const radius = object.radius || 60;
    const innerRadius = radius * 0.6;
    const path = `
      M ${radius} 0
      A ${radius} ${radius} 0 0 1 ${-radius} 0
      A ${innerRadius} ${innerRadius} 0 0 0 ${radius} 0
      Z
    `;
    const instance = new fabric.Crescent({
      ...object,
      path: path,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      originX: 'center',
      originY: 'center',
      radius: radius,
      id: object.id,
      type: object.type,
    });
    callback && callback(instance);
  };

  fabric.Object._fromObject['crescent'] = fabric.Crescent;
}